import localforage from "localforage";
import { defineStore } from "pinia";
import { HousingSiteDetailDto } from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";

export const useHousingSiteDetailStore = defineStore("HousingSiteDetailStore", {
  state: (): {
    List: HousingSiteDetailDto[];
    storeLoaded: boolean;
    isRefreshing: boolean;
    lastLoadTime: number;
  } => {
    return {
      List: [],
      storeLoaded: false,
      isRefreshing: false,
      lastLoadTime: 0,
      // setting lastLoadTime to 0 forces a refresh on browser page reload. This makes the cache only effective during the same page load.
      // setting to Date.now() will make the cache effective for the duration of the cache time.
    };
  },
  actions: {
    /** Local store for HousingSiteDetails. This store will cache the data in local storage.
     * @example
     * ```ts
     * import {useHousingSiteDetailStore} from "~/YYC_Housing/Intake/stores/HousingSiteDetailStore";
     * const HousingSiteDetailStore = useHousingSiteDetailStore();
     *
     * onMounted(async () => {
     *  await useHousingSiteDetailStore.load();
     * });
     *
     * const list = houseSiteStore.List; // use the list from the store (no need to await)
     * const siteName = HousingSiteDetailStore.getName("YYC"); // use a store function
     * ```
     * @param {boolean} forceUpdate - Can set to true to force a reload from the server.
     */
    async load(forceUpdate: boolean = false) {
      if (this.isRefreshing) {
        // return a promise that will resolve when the store is done refreshing
        return new Promise<void>((resolve, _) => {
          const checkRefreshing = setInterval(() => {
            if (!this.isRefreshing) {
              clearInterval(checkRefreshing);
              resolve();
            }
          }, 100); // adjust this if needed
        });
      }

      this.isRefreshing = true; // set this to true as soon as possible to prevent multiple parallel calls

      // set to 10 minutes
      const MaxCacheTime = 10 * 60 * 1000;

      // this is not a sliding expiration, it is a hard expiration. Cache isn't actually gone, but we will refresh it.
      if (Date.now() - this.lastLoadTime > MaxCacheTime) {
        forceUpdate = true;
      }

      if (!forceUpdate && this.List.length !== 0) {
        this.isRefreshing = false;
        return;
      }

      try {
        const cached = await localforage.getItem(this.$id);
        if (cached && !forceUpdate) {
          this.List = JSON.parse(cached as string);
          this.storeLoaded = true;
          this.isRefreshing = false;
          return;
        }

        // show this message only if we are getting fresh data (keep the console.log)
        console.log("getting fresh", this.$id);

        // call the correct apiClient method
        const myInfoStore = useMyInfoStore();
        await myInfoStore.load();

        this.List = await apiClient.housingSiteDetails.getAllDetails(
          myInfoStore.currentSysOrgGuid,
        );

        localforage.setItem(this.$id, JSON.stringify(this.List));
        this.storeLoaded = true;
        this.isRefreshing = false;
        this.lastLoadTime = Date.now();
      } catch (ex) {
        this.List = [];
        this.storeLoaded = false;
        this.isRefreshing = false;
        throw ex;
      }
    },
    /** Call this after making a change to the underlying data. The next use of the store will get a fresh copy. */
    clear() {
      this.List = [];
      this.storeLoaded = false;
      localforage.removeItem(this.$id);
    },
  },
});
